import { useFormikSubmit } from "@certn/api";
import { isAddressErrors, useAddressSchema } from "@certn/cascadia";
import {
  AddressForm,
  ButtonGroup,
  DateSelectorFormikField,
  Grid,
  GridItem,
  PrimaryButton,
} from "@certn/cascadia-react";
import { Granularity } from "@certn/cascadia-react/components/inputs/date-input/constants";
import { Form, Formik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { date, object } from "yup";

import { useProfile } from "../../../auth/ProfileProvider";
import { SettingsEditItem } from "../../../components/settings-edit/SettingsEditItem";
import { Schemas } from "../../../types";

function formatAddress(
  address?: Schemas["PersonalInformationProfileaddressSet"],
  defaultValue = "-"
) {
  if (!address) {
    return defaultValue;
  }

  return [address.street_address, address.locality].filter(Boolean).join(", ");
}

export function AddressSettings() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded((curr) => !curr);
  const { profile, refresh } = useProfile();
  const addressSchema = useAddressSchema();

  const address = profile?.profileaddress_set[0];

  const { submit } = useFormikSubmit(
    "/api/internal/applicant/me/personal-info/current-address/update/",
    {
      onSuccess: () => {
        toggle();
        refresh();
      },
      showToastOnError: true,
    }
  );

  return (
    <SettingsEditItem
      setting={t("Home address")}
      value={formatAddress(address, t("Not provided"))}
      description={t("Use your current home address")}
      expanded={expanded}
      toggle={toggle}
      testID={expanded ? "home-address-cancel-link" : "home-address-edit-link"}
    >
      <Formik
        initialValues={
          address || {
            unit_number: "",
            street_address: "",
            locality: "",
            administrative_division_1: "",
            country: "",
            administrative_division_2: "",
            postal_code: "",
            start_date: "",
          }
        }
        onSubmit={submit}
        validationSchema={addressSchema.concat(
          object().shape({
            start_date: date().required().label(t("Start date")),
          })
        )}
      >
        {({ values, setValues, errors, isSubmitting }) => {
          return (
            <Form>
              <Grid width="100%" form>
                <AddressForm
                  value={values}
                  onChange={(value) => setValues({ ...values, ...value })}
                  errors={isAddressErrors(errors) ? errors : undefined}
                  testID="address-settings"
                />
                <GridItem>
                  <DateSelectorFormikField
                    label={t("Start date")}
                    name="start_date"
                    granularity={Granularity.month}
                    isRequired
                    fullWidth
                    formatOverride="mm-yy"
                    testID="address-start-date"
                  />
                </GridItem>
                <GridItem>
                  <ButtonGroup>
                    <PrimaryButton
                      type="submit"
                      size="medium"
                      testID="address-form-save-button"
                      isLoading={isSubmitting}
                    >
                      {t("Save")}
                    </PrimaryButton>
                  </ButtonGroup>
                </GridItem>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </SettingsEditItem>
  );
}
